import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ResourceCard from "@/components/feature/ResourceCard";
import { animeResources, categories, featuredIds } from "@/mocks/resources";
import { faqs } from "@/mocks/faqs";

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  const featured = featuredIds
    .map((id) => animeResources.find((r) => r.id === id))
    .filter(Boolean) as typeof animeResources;

  const latest = [...animeResources]
    .sort((a, b) => b.updateTime.localeCompare(a.updateTime))
    .slice(0, 8);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) navigate(`/library?q=${encodeURIComponent(keyword.trim())}`);
  };

  return (
    <main>
      {/* ===== 首屏 ===== */}
      <section className="relative min-h-[560px] md:min-h-[640px] flex items-center">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20anime%20fantasy%20cityscape%20at%20golden%20sunset%2C%20rolling%20warm%20hills%20and%20floating%20clouds%2C%20a%20silhouette%20of%20adventurers%20on%20a%20road%2C%20vivid%20banana%20yellow%20amber%20and%20soft%20coral%20sky%2C%20dreamy%20atmospheric%20anime%20background%20art%2C%20wide%20cinematic%20composition%2C%20warm%20and%20inviting%20tone&width=1600&height=900&seq=hero-main&orientation=landscape"
          alt="香蕉动漫资源聚合站首页背景图"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/40 to-background-50"></div>

        <div className="relative z-10 mx-auto max-w-4xl w-full px-4 md:px-6 text-center pt-24 pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/15 text-background-50 text-xs tracking-wide">
            <i className="ri-sparkling-2-line" aria-hidden="true"></i>
            香蕉动漫 · 追更不迷路
          </span>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl leading-tight text-background-50">
            一站找齐你的
            <span className="text-primary-400"> 动漫全集资源</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-background-100/90 max-w-2xl mx-auto leading-7">
            香蕉动漫资源合集页，汇总热门动漫全集资源与资源站目录，从热血冒险到日常治愈，热门番剧的合集分享与更新动态都在这里。
          </p>

          <form
            onSubmit={submitSearch}
            className="mt-8 mx-auto max-w-xl bg-background-50 rounded-full p-1.5 flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 shadow-lg"
            role="search"
          >
            <div className="flex-1 flex items-center gap-2 px-4">
              <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你想看的动漫，如：进击的巨人"
                className="w-full bg-transparent text-sm py-2 outline-none text-foreground-950 placeholder:text-foreground-500"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始搜索
            </button>
          </form>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {[
              { num: "600+", label: "整理资源" },
              { num: "6 大", label: "主题分类" },
              { num: "每日", label: "更新动态" },
            ].map((s) => (
              <div key={s.label} className="text-background-50">
                <p className="font-heading text-2xl">{s.num}</p>
                <p className="text-xs text-background-100/80 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 热门推荐 ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-accent-600 tracking-wide">HOT PICKS</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">热门动漫推荐</h2>
          </div>
          <Link
            to="/library"
            className="inline-flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            查看全部资源 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {featured.map((r) => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>
      </section>

      {/* ===== 主题分类 ===== */}
      <section className="bg-background-100/70 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-medium text-accent-600 tracking-wide">CATEGORIES</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">按主题分类找番</h2>
            <p className="mt-3 text-sm text-foreground-700 max-w-xl mx-auto">
              从热血燃向到日常治愈，六大主题分类帮你有条理地补齐想看的动漫合集。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/library?cat=${c.id}`}
                className="group flex items-center gap-4 bg-background-50 border border-background-200 rounded-2xl p-5 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="w-12 h-12 shrink-0 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-base text-foreground-950 whitespace-nowrap">{c.name}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap">
                      {c.count} 部
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-foreground-600 leading-5 line-clamp-1">{c.desc}</p>
                </div>
                <i
                  className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-600 text-xl"
                  aria-hidden="true"
                ></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 最新更新 ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-accent-600 tracking-wide">LATEST</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">最新更新合集</h2>
          </div>
          <span className="text-sm text-foreground-600 whitespace-nowrap">更新至 {latest[0]?.updateTime}</span>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {latest.map((r) => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>
      </section>

      {/* ===== 特色亮点 ===== */}
      <section className="bg-secondary-50 border-y border-secondary-200 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-medium text-accent-600 tracking-wide">WHY US</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">为什么选择香蕉动漫</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: "ri-stack-line", title: "全集资源整理", desc: "热门动漫全集资源系统整理，按篇章分类，补番更省心。" },
              { icon: "ri-refresh-line", title: "追更不迷路", desc: "连载番剧更新动态第一时间同步，再也不用担心断更。" },
              { icon: "ri-list-check-2", title: "目录清晰易懂", desc: "分类 + 搜索 + 画质说明，帮你快速锁定想看的那一部。" },
              { icon: "ri-device-line", title: "全端流畅体验", desc: "响应式设计，电脑平板手机都能顺畅浏览与检索。" },
            ].map((f) => (
              <div
                key={f.title}
                className="bg-background-50 border border-background-200 rounded-2xl p-6 hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className={`${f.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-6">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ 预览 ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs font-medium text-accent-600 tracking-wide">FAQ</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">常见问题</h2>
            <p className="mt-3 text-sm text-foreground-700 leading-7 max-w-md">
              关于香蕉动漫资源合集的使用方式、更新频率与浏览体验，这里汇总了大家最常问到的问题。
            </p>
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              查看全部问题 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="space-y-3">
            {faqs.slice(0, 4).map((f) => (
              <details
                key={f.id}
                className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none text-sm font-medium text-foreground-950">
                  {f.question}
                  <i
                    className="ri-add-line text-foreground-500 group-open:rotate-45 transition-transform shrink-0"
                    aria-hidden="true"
                  ></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-700 leading-6">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 pb-4">
        <div className="relative overflow-hidden rounded-3xl bg-primary-500 px-6 py-12 md:py-14 text-center">
          <i
            className="ri-quill-pen-fill absolute -left-6 -top-6 text-primary-300 text-8xl rotate-12"
            aria-hidden="true"
          ></i>
          <i
            className="ri-play-circle-fill absolute -right-4 -bottom-8 text-primary-300 text-9xl"
            aria-hidden="true"
          ></i>
          <div className="relative">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">找番补番，从这里开始</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-800 max-w-xl mx-auto leading-7">
              前往香蕉动漫资源库，按分类浏览或直接搜索，一站找齐你的动漫全集资源。
            </p>
            <Link
              to="/library"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              进入资源库 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}